import { FilePdfOutlined } from '@ant-design/icons'
import type { Home } from '@lmc/ts-common'
import { EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general'
import useSafeguardingAccess from 'components/AccessControl/useSafeguardingAccess'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import useHasSafeguardingEnabled from 'hooks/useHasSafeguardingEnabled'
import usePdfExport from 'hooks/usePdfExport'
import type jsPDF from 'jspdf'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import logo from '../../../../../images/LMC-Logo-Pos-RGB 1.png'
import { IncidentFilters } from '../../components/IncidentFilters'
import SafeguardingBanner from '../../components/SafeguardingBanner'
import { useIncidentsReportList } from '../../hooks'
import type { FilterValues } from '../../hooks/useIncidentsReportList'
import { IncidentsCharts } from './IncidentsCharts'

const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(4, 'week').startOf('day'), dayjs()],
  residents: [],
  locations: [],
  categoryItems: [],
  statuses: [],
  severities: [],
  resultedIn: []
}

export type IncidentsReportMainProps = {
  title?: string
  description?: string
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasOnClick?: boolean
  testId?: string
}

export type SetFilterParams = (f: (filterParams: FilterValues) => void) => void

export const IncidentsDashboard = ({
  description,
  hasResidentsFilter,
  hasHomesFilter,
  testId = 'incidents-dashboard'
}: IncidentsReportMainProps) => {
  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric(initialFiltersValue)

  const filterParams = filterParamsGeneric as FilterValues

  const { data: flags } = useFeatureFlags({
    flagNames: ['safeguarding']
  })
  const isSafeguardingEnabled = flags?.safeguarding?.enabled

  const {
    data: incidents,
    isLoading,
    refetch
  } = useIncidentsReportList(filterParams as FilterValues, 'dashboard')

  useEffect(() => {
    refetch()
  }, [])

  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasIncidents = !!(incidents && Object.keys(incidents).length)

  const [isDownloading, setIsDownloading] = useState(false)

  const generatePDFHeader = (pdf: jsPDF) => {
    pdf.setFont('Helvetica')
    pdf.setFontSize(24)
    pdf.text(`${t(incidentReportsConstants.INCIDENTS_DASHBOARD)} - PDF`, 10, 20)
    pdf.addImage(logo, 'JPEG', 170, 15, 140 / 4, 25 / 4)
    pdf.setDrawColor('#D9D9D9')
    pdf.setLineWidth(0.25)
    pdf.line(10, 25, 220, 25)
    pdf.setFontSize(18)
    pdf.text(`${t(incidentReportsConstants.SITE)}: ${home.name}`, 10, 35)
  }

  const pdfContent = useRef<HTMLInputElement>(null)
  const filtersRef = useRef<HTMLInputElement>(null)
  const handleDownloadPdf = usePdfExport(
    [filtersRef, pdfContent],
    `${t(incidentReportsConstants.INCIDENTS_DASHBOARD)}:${home.name}`,
    setIsDownloading,
    1,
    generatePDFHeader,
    35
  )

  const safeguardingAccess = useSafeguardingAccess()
  const hasSafeguardingHook = useHasSafeguardingEnabled()

  const hasSafeguardingEnabled = isSafeguardingEnabled && hasSafeguardingHook

  const displaySafeguardingBanner =
    hasSafeguardingEnabled && !safeguardingAccess

  return (
    <>
      <PageHeader
        testId={`${testId}`}
        title={
          <TitleContainer data-testid={`${testId}-title-container`}>
            {t(incidentReportsConstants.INCIDENTS_DASHBOARD)}
          </TitleContainer>
        }
        subtitle={description}
      >
        <Button
          onClick={handleDownloadPdf}
          isLoading={isDownloading}
          data-testid={`${testId}-download-pdf-button`}
        >
          <FilePdfOutlined />
          {t(incidentReportsConstants.DOWNLOAD_PDF)}
        </Button>
      </PageHeader>
      <IncidentFilters
        data-testid={`${testId}-filters`}
        filterParams={filterParams}
        setFilterParams={setFilterParams as SetFilterParams}
        hasResidentsFilter={hasResidentsFilter}
        hasHomesFilter={hasHomesFilter}
      />
      {displaySafeguardingBanner && <SafeguardingBanner />}
      {hasIncidents && (
        <IncidentsCharts
          data-testid={`${testId}-charts`}
          filterParams={filterParams}
          ref={pdfContent}
        />
      )}
      {!isLoading && !hasIncidents && (
        <EmptyState
          data-testid={`${testId}-empty-state`}
          title={t(incidentReportsConstants.NO_DATA)}
          icon={<NoDataIcon />}
        />
      )}
      {isLoading && <Spinner data-testid={`${testId}-spinner`} />}
    </>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
